import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import {
	useGetEligibleTimesheetsForBillingLazyQuery,
	useRequestMonthlyBillingMutation,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { forwardRef, useCallback, useImperativeHandle, useMemo, useRef, useState } from "react";

import type { EligibleTimesheetForBilling } from "../types/MonthlyProjectOverviewTypes";

export type MonthlyBillingDialogShowArgs = {
	projectId: string;
	projectName: string;
	month: string;
	approvedTimesheetCount: number;
	projectType: string | null;
	onSuccess?: () => void;
};

export type MonthlyBillingDialogHandle = {
	show: (args: MonthlyBillingDialogShowArgs) => void;
	close: () => void;
};

export const MonthlyBillingDialog = forwardRef<MonthlyBillingDialogHandle>((_props, ref) => {
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	const [open, setOpen] = useState(false);
	const [options, setOptions] = useState<MonthlyBillingDialogShowArgs | null>(null);
	const projectTypeRef = useRef<string | null>(null);

	// Form state - separate selections for invoice, credit notes, and contractor credit notes
	const [selectedForInvoice, setSelectedForInvoice] = useState<string[]>([]);
	const [selectedForCreditNotes, setSelectedForCreditNotes] = useState<string[]>([]);
	const [selectedForContractorCreditNotes, setSelectedForContractorCreditNotes] = useState<string[]>([]);
	const [eligibleTimesheets, setEligibleTimesheets] = useState<EligibleTimesheetForBilling[]>([]);

	// GraphQL queries/mutations
	const [getEligibleTimesheets, { loading: loadingTimesheets }] = useGetEligibleTimesheetsForBillingLazyQuery({
		fetchPolicy: "no-cache",
		onCompleted: (data) => {
			const timesheets: EligibleTimesheetForBilling[] = (data.eligibleTimesheetsForBilling ?? []).map((ts) => ({
				timesheetId: ts.timesheetId,
				number: ts.number,
				employeeId: ts.employee.id,
				employeeName: ts.employee.displayName,
				from: ts.from,
				to: ts.to,
				totalHours: ts.totalHours,
				requiresCreditNote: ts.requiresCreditNote,
				hasClientInvoice: ts.hasClientInvoice,
				hasSupplierCreditNote: ts.hasSupplierCreditNote,
				requiresContractorCreditNote: ts.requiresContractorCreditNote,
				hasContractorCreditNote: ts.hasContractorCreditNote,
			}));
			setEligibleTimesheets(timesheets);
			// Pre-select timesheets without invoice for invoice list (skip for internal projects)
			const isInternal = projectTypeRef.current === "INTERNAL";
			setSelectedForInvoice(isInternal ? [] : timesheets.filter((ts) => !ts.hasClientInvoice).map((ts) => ts.timesheetId));
			// Pre-select timesheets that require credit note and don't have one
			setSelectedForCreditNotes(
				timesheets
					.filter((ts) => ts.requiresCreditNote && !ts.hasSupplierCreditNote)
					.map((ts) => ts.timesheetId),
			);
			// Pre-select timesheets that require contractor credit note and don't have one
			setSelectedForContractorCreditNotes(
				timesheets
					.filter((ts) => ts.requiresContractorCreditNote && !ts.hasContractorCreditNote)
					.map((ts) => ts.timesheetId),
			);
		},
		onError: (error) => {
			showNotification(error.message, "error");
		},
	});

	const [requestMonthlyBilling, { loading: submitting }] = useRequestMonthlyBillingMutation({
		onCompleted: (data) => {
			if (data.requestMonthlyBilling.errors && data.requestMonthlyBilling.errors.length > 0) {
				const errorMessages = data.requestMonthlyBilling.errors.map((e) => e.message).join(", ");
				showNotification(errorMessages, "error");
				return;
			}
			showNotification(t("billing-created-success"), "success");
			handleCloseInternal();
			options?.onSuccess?.();
		},
		onError: (error) => {
			showNotification(error.message, "error");
		},
	});

	// Expose imperative API
	useImperativeHandle(
		ref,
		() => ({
			show: (args: MonthlyBillingDialogShowArgs) => {
				setOptions(args);
				projectTypeRef.current = args.projectType;
				setSelectedForInvoice([]);
				setSelectedForCreditNotes([]);
				setSelectedForContractorCreditNotes([]);
				setEligibleTimesheets([]);
				setOpen(true);

				// Load eligible timesheets
				getEligibleTimesheets({
					variables: {
						projectId: args.projectId,
						month: args.month,
					},
				});
			},
			close: () => {
				handleCloseInternal();
			},
		}),
		[getEligibleTimesheets],
	);

	const handleCloseInternal = useCallback(() => {
		setOpen(false);
		setOptions(null);
	}, []);

	// Timesheets available for each type
	const selectableForInvoice = useMemo(
		() => eligibleTimesheets.filter((ts) => !ts.hasClientInvoice),
		[eligibleTimesheets],
	);

	const selectableForCreditNotes = useMemo(
		() => eligibleTimesheets.filter((ts) => ts.requiresCreditNote && !ts.hasSupplierCreditNote),
		[eligibleTimesheets],
	);

	const selectableForContractorCreditNotes = useMemo(
		() => eligibleTimesheets.filter((ts) => ts.requiresContractorCreditNote && !ts.hasContractorCreditNote),
		[eligibleTimesheets],
	);

	// Check if any contractor credit notes are possible
	const hasContractorCreditNoteColumn = useMemo(
		() => eligibleTimesheets.some((ts) => ts.requiresContractorCreditNote),
		[eligibleTimesheets],
	);

	const toggleInvoice = useCallback((timesheetId: string) => {
		setSelectedForInvoice((prev) =>
			prev.includes(timesheetId) ? prev.filter((id) => id !== timesheetId) : [...prev, timesheetId],
		);
	}, []);

	const toggleCreditNote = useCallback((timesheetId: string) => {
		setSelectedForCreditNotes((prev) =>
			prev.includes(timesheetId) ? prev.filter((id) => id !== timesheetId) : [...prev, timesheetId],
		);
	}, []);

	const toggleContractorCreditNote = useCallback((timesheetId: string) => {
		setSelectedForContractorCreditNotes((prev) =>
			prev.includes(timesheetId) ? prev.filter((id) => id !== timesheetId) : [...prev, timesheetId],
		);
	}, []);

	const handleSelectAllInvoice = useCallback(() => {
		if (selectedForInvoice.length === selectableForInvoice.length) {
			setSelectedForInvoice([]);
		} else {
			setSelectedForInvoice(selectableForInvoice.map((ts) => ts.timesheetId));
		}
	}, [selectableForInvoice, selectedForInvoice]);

	const handleSelectAllCreditNotes = useCallback(() => {
		if (selectedForCreditNotes.length === selectableForCreditNotes.length) {
			setSelectedForCreditNotes([]);
		} else {
			setSelectedForCreditNotes(selectableForCreditNotes.map((ts) => ts.timesheetId));
		}
	}, [selectableForCreditNotes, selectedForCreditNotes]);

	const handleSelectAllContractorCreditNotes = useCallback(() => {
		if (selectedForContractorCreditNotes.length === selectableForContractorCreditNotes.length) {
			setSelectedForContractorCreditNotes([]);
		} else {
			setSelectedForContractorCreditNotes(selectableForContractorCreditNotes.map((ts) => ts.timesheetId));
		}
	}, [selectableForContractorCreditNotes, selectedForContractorCreditNotes]);

	const handleSubmit = useCallback(() => {
		if (!options) return;

		requestMonthlyBilling({
			variables: {
				projectId: options.projectId,
				month: options.month,
				timesheetIdsForInvoice: selectedForInvoice,
				timesheetIdsForCreditNotes: selectedForCreditNotes,
				timesheetIdsForContractorCreditNotes: selectedForContractorCreditNotes,
			},
		});
	}, [options, selectedForInvoice, selectedForCreditNotes, selectedForContractorCreditNotes, requestMonthlyBilling]);

	const isInternalProject = options?.projectType === "INTERNAL";

	const canSubmit =
		selectedForInvoice.length > 0 || selectedForCreditNotes.length > 0 || selectedForContractorCreditNotes.length > 0;

	const totalInvoiceHours = selectedForInvoice.reduce((sum, id) => {
		const ts = eligibleTimesheets.find((t) => t.timesheetId === id);
		return sum + (ts?.totalHours ?? 0);
	}, 0);

	const totalCreditNoteHours = selectedForCreditNotes.reduce((sum, id) => {
		const ts = eligibleTimesheets.find((t) => t.timesheetId === id);
		return sum + (ts?.totalHours ?? 0);
	}, 0);

	const totalContractorCreditNoteHours = selectedForContractorCreditNotes.reduce((sum, id) => {
		const ts = eligibleTimesheets.find((t) => t.timesheetId === id);
		return sum + (ts?.totalHours ?? 0);
	}, 0);

	const formatDateRange = (from: string, to: string) => {
		const fromDate = new Date(from);
		const toDate = new Date(to);
		return `${fromDate.toLocaleDateString()} - ${toDate.toLocaleDateString()}`;
	};

	// Helper to check if a timesheet can be selected for each type
	const canSelectForInvoice = (ts: EligibleTimesheetForBilling) => !ts.hasClientInvoice;
	const canSelectForCreditNote = (ts: EligibleTimesheetForBilling) =>
		ts.requiresCreditNote && !ts.hasSupplierCreditNote;
	const canSelectForContractorCreditNote = (ts: EligibleTimesheetForBilling) =>
		ts.requiresContractorCreditNote && !ts.hasContractorCreditNote;

	return (
		<Dialog open={open} maxWidth="lg" fullWidth data-testid="monthly-billing-dialog">
			<DialogTitle>{t("monthly-billing-for-project", { project: options?.projectName ?? "" })}</DialogTitle>
			<DialogContent>
				{/* Summary Alerts */}
				<Stack direction="row" spacing={2} sx={{ mb: 2 }}>
					{!isInternalProject && (
						<Alert severity="info" sx={{ flex: 1 }}>
							<Typography variant="body2">
								<strong>{t("client-invoice")}:</strong> {totalInvoiceHours.toFixed(1)} {t("hours")}
							</Typography>
						</Alert>
					)}
					<Alert severity="info" sx={{ flex: 1 }}>
						<Typography variant="body2">
							<strong>{t("supplier-credit-notes")}:</strong> {totalCreditNoteHours.toFixed(1)} {t("hours")}
						</Typography>
					</Alert>
					{hasContractorCreditNoteColumn && (
						<Alert severity="info" sx={{ flex: 1 }}>
							<Typography variant="body2">
								<strong>{t("contractor-credit-notes")}:</strong> {totalContractorCreditNoteHours.toFixed(1)}{" "}
								{t("hours")}
							</Typography>
						</Alert>
					)}
				</Stack>

				{loadingTimesheets ? (
					<Typography color="text.secondary">{t("loading")}</Typography>
				) : eligibleTimesheets.length === 0 ? (
					<Typography color="text.secondary">{t("no-timesheets-found")}</Typography>
				) : (
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell sx={{ fontWeight: "bold" }}>{t("timesheet")}</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>{t("employee")}</TableCell>
								<TableCell sx={{ fontWeight: "bold" }}>{t("period")}</TableCell>
								<TableCell align="right" sx={{ fontWeight: "bold" }}>
									{t("hours")}
								</TableCell>
								{!isInternalProject && (
									<TableCell align="center" sx={{ fontWeight: "bold", minWidth: 120 }}>
										<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
											<span>{t("invoice")}</span>
											{selectableForInvoice.length > 0 && (
												<Button size="small" onClick={handleSelectAllInvoice} sx={{ fontSize: "0.7rem", p: 0 }}>
													{selectedForInvoice.length === selectableForInvoice.length
														? t("deselect-all")
														: t("select-all")}
												</Button>
											)}
										</Box>
									</TableCell>
								)}
								<TableCell align="center" sx={{ fontWeight: "bold", minWidth: 120 }}>
									<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
										<span>{t("credit-note-short")}</span>
										{selectableForCreditNotes.length > 0 && (
											<Button size="small" onClick={handleSelectAllCreditNotes} sx={{ fontSize: "0.7rem", p: 0 }}>
												{selectedForCreditNotes.length === selectableForCreditNotes.length
													? t("deselect-all")
													: t("select-all")}
											</Button>
										)}
									</Box>
								</TableCell>
								{hasContractorCreditNoteColumn && (
									<TableCell align="center" sx={{ fontWeight: "bold", minWidth: 120 }}>
										<Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
											<span>{t("contractor-cn-short")}</span>
											{selectableForContractorCreditNotes.length > 0 && (
												<Button
													size="small"
													onClick={handleSelectAllContractorCreditNotes}
													sx={{ fontSize: "0.7rem", p: 0 }}>
													{selectedForContractorCreditNotes.length === selectableForContractorCreditNotes.length
														? t("deselect-all")
														: t("select-all")}
												</Button>
											)}
										</Box>
									</TableCell>
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{eligibleTimesheets.map((timesheet) => (
								<TableRow
									key={timesheet.timesheetId}
									sx={{
										"&:hover": { bgcolor: "action.hover" },
									}}>
									<TableCell>{timesheet.number}</TableCell>
									<TableCell>{timesheet.employeeName}</TableCell>
									<TableCell>{formatDateRange(timesheet.from, timesheet.to)}</TableCell>
									<TableCell align="right">{timesheet.totalHours.toFixed(1)}</TableCell>

									{/* Invoice Column */}
									{!isInternalProject && (
										<TableCell align="center">
											{canSelectForInvoice(timesheet) ? (
												<Checkbox
													checked={selectedForInvoice.includes(timesheet.timesheetId)}
													onChange={() => toggleInvoice(timesheet.timesheetId)}
													data-testid={`invoice-checkbox-${timesheet.timesheetId}`}
												/>
											) : (
												<Chip label={t("has-invoice")} color="success" size="small" variant="outlined" />
											)}
										</TableCell>
									)}

									{/* Credit Note Column */}
									<TableCell align="center">
										{canSelectForCreditNote(timesheet) ? (
											<Checkbox
												checked={selectedForCreditNotes.includes(timesheet.timesheetId)}
												onChange={() => toggleCreditNote(timesheet.timesheetId)}
												data-testid={`creditnote-checkbox-${timesheet.timesheetId}`}
											/>
										) : timesheet.hasSupplierCreditNote ? (
											<Chip label={t("has-credit-note")} color="success" size="small" variant="outlined" />
										) : !timesheet.requiresCreditNote ? (
											<Chip label={t("not-required")} color="default" size="small" variant="outlined" />
										) : null}
									</TableCell>

									{/* Contractor Credit Note Column */}
									{hasContractorCreditNoteColumn && (
										<TableCell align="center">
											{canSelectForContractorCreditNote(timesheet) ? (
												<Checkbox
													checked={selectedForContractorCreditNotes.includes(timesheet.timesheetId)}
													onChange={() => toggleContractorCreditNote(timesheet.timesheetId)}
													data-testid={`contractor-creditnote-checkbox-${timesheet.timesheetId}`}
												/>
											) : timesheet.hasContractorCreditNote ? (
												<Chip label={t("has-credit-note")} color="success" size="small" variant="outlined" />
											) : !timesheet.requiresContractorCreditNote ? (
												<Chip label={t("not-required")} color="default" size="small" variant="outlined" />
											) : null}
										</TableCell>
									)}
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={handleCloseInternal} disabled={submitting} data-testid="monthly-billing-cancel-button">
					{t("cancel")}
				</Button>
				<Button
					onClick={handleSubmit}
					variant="contained"
					disabled={!canSubmit || submitting}
					data-testid="monthly-billing-submit-button">
					{submitting ? t("loading") : t("create-billing")}
				</Button>
			</DialogActions>
		</Dialog>
	);
});
